/**
 * GenericSampleLabelDialog — Sample Registration ▸ "Generic Sample Label".
 *
 * A 1:1 port of the legacy ALPACA (XAF) popup `PrintBarcode_DashboardView`,
 * driven by `BTLIMS.Module.Web/Controllers/SampleRegistration/
 * PrintBarcodeLabelViewController.cs` and its two non-persistent business
 * objects `NPPrintBarcodeHeader` / `NPPrintBarcodeBottle`.
 *
 * ── LEGACY BEHAVIOUR REPRODUCED EXACTLY ─────────────────────────────────────
 *  · NOTHING is persisted. Both legacy BOs are `[DomainComponent]` and the rows
 *    live in a plain in-memory list; the popup opens with
 *    `DialogController.SaveOnAccept = false` and its OK/Cancel actions hidden.
 *    So this dialog holds its rows in React state and writes no database row.
 *  · Prefill — Job ID from the current Samplecheckin; Visual Matrix from
 *    `Samplecheckin.GroupVisualMatrix`, falling back to the first SampleLogIn of
 *    the job that HAS a VisualMatrix; No. of Samples = 1; Default Qty/Sample = 1.
 *  · Display — validates in the legacy order with the legacy wording, then
 *    generates `{JobID}-{s:000}` x `DefaultQtyPerSample` bottles lettered
 *    A, B, ... Z, AA, AB ... (bijective base-26, legacy `ToBottleLetters`).
 *  · Editing `VisualMatrix` on one row copies it to every row of the SAME
 *    SampleID (legacy `PropagateVisualMatrix`). Storage is NOT propagated —
 *    legacy doesn't either.
 *  · Editing `NoOfQty` re-shapes that sample's bottle rows and re-letters them
 *    (legacy `ApplyBottleCount`).
 *  · Right-click ▸ "Copy To All Cell" copies the clicked cell's value into every
 *    TICKED row (legacy `FillContextMenuItems` + `ContextMenuItemClick`).
 *  · Refresh — clears the grid and resets the Details panel, KEEPING the Job ID.
 *  · Print Preview — blocked until Display has run, then opens the PDF in a new
 *    tab as a blob (legacy exported the XtraReport to PDF and did exactly this).
 *  · Exit — closes the popup.
 *
 * ── AI STANDARDS APPLIED ────────────────────────────────────────────────────
 *  Shared `<AppDialog>` (rule 12 chrome), shared `<AppDataGrid>` (rule 7),
 *  shared `<ComboSelect>` lookups, shared `useNotify` messages (rule 7), and
 *  the PDF is rendered SERVER-side through `IPdfReportGenerator<T>` (rule 8) —
 *  never drawn in the browser.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { api } from '../../../api/client';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ComboSelect } from '../../../components/ComboSelect';
import { useNotify } from '../../../components/notifications';
import {
  LABEL_REPORT_TYPES, applyBottleCount, emptyLabelHeader, generatePreview,
  propagateVisualMatrix, spin,
  type LabelHeader, type LabelRow,
} from './genericSampleLabelLogic';
import './genericSampleLabel.css';

/**
 * The report-designer template this popup prints through, or null to use ALPACA's
 * code-built label layout. Set this to a `colCustomReportDesignerName` and Print
 * Preview switches to the shared Custom Reporting / MOR render pipeline — no other
 * change needed. Null today: no label template exists for this form (see below).
 */
const LABEL_TEMPLATE_NAME: string | null = null;

interface Props {
  open: boolean;
  /** The saved Job ID of the current registration (legacy `Samplecheckin.JobID`). */
  jobId: string;
  /** `Samplecheckin.GroupVisualMatrix`, or '' when the header has none. */
  groupVisualMatrix?: string;
  /** Fallback — the first sample of the job that carries a VisualMatrix
   *  (legacy `FindObject<SampleLogIn>("[JobID.Oid] = ? And [VisualMatrix] Is Not Null")`). */
  firstSampleVisualMatrix?: string;
  /** VisualMatrixName options (dbo.VisualMatrix, via the form's `matrix` lookup). */
  matrixOptions: string[];
  onClose: () => void;
}


export function GenericSampleLabelDialog({
  open, jobId, groupVisualMatrix, firstSampleVisualMatrix, matrixOptions, onClose,
}: Props) {
  const notify = useNotify();

  const prefillMatrix = groupVisualMatrix || firstSampleVisualMatrix || '';
  const [header, setHeader] = useState<LabelHeader>(() => emptyLabelHeader(jobId, prefillMatrix));
  const [rows, setRows] = useState<LabelRow[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [reportType, setReportType] = useState<string>(LABEL_REPORT_TYPES[0]);
  const [storages, setStorages] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState<
    { x: number; y: number; field: 'noOfQty' | 'visualMatrix' | 'storage'; value: string } | null>(null);

  // Reopen fresh — legacy creates a new NonPersistentObjectSpace on every click.
  const openedRef = useRef(false);
  useEffect(() => {
    if (open && !openedRef.current) {
      openedRef.current = true;
      setHeader(emptyLabelHeader(jobId, prefillMatrix));
      setRows([]);
      setSelected(new Set());
      setReportType(LABEL_REPORT_TYPES[0]);
    }
    if (!open) openedRef.current = false;
  }, [open, jobId, prefillMatrix]);

  /**
   * Report selector. ALPACA hardcodes the two variants as ChoiceActionItems
   * (`PrintBarcodeLabelViewController` lines 68-69) and DRAWS THE LABEL IN CODE —
   * there is no report-designer row behind either, verified on the live DB: no
   * template is named "BarcodeID Label"/"SampleID Label", and the label templates
   * that DO exist (BarcodeSampleID, SampleReceiving_VerticalBarcode, FolderLabel)
   * are assigned to other forms. So the list stays the legacy pair.
   *
   * The designer path IS wired and one step away: give a row a `templateName` and
   * POST /label-pdf renders it through the shared Custom Reporting / MOR pipeline
   * instead of the built-in layout (see IGenericSampleLabelReportRenderer).
   */

  // Storage list — dbo.Storage via the shared Containers lookup endpoint.
  useEffect(() => {
    if (!open || storages.length > 0) return;
    let alive = true;
    api.get('/api/v1/sample-registration/bottle-lookups')
      .then(res => {
        const d = (res.data as { data?: { storages?: { name?: string }[] } })?.data;
        const names = (d?.storages ?? []).map(s => s.name ?? '').filter(Boolean);
        if (alive) setStorages(names);
      })
      .catch(() => { /* the axios interceptor reports it; the list stays empty */ });
    return () => { alive = false; };
  }, [open, storages.length]);

  const patch = useCallback(
    (p: Partial<LabelHeader>) => setHeader(h => ({ ...h, ...p })), []);

  // ── Actions ───────────────────────────────────────────────────────────────

  /** Legacy `btnDisplay_Execute` — same checks, same order, same wording. */
  const doDisplay = useCallback(() => {
    if (!header.jobId) { notify.warning('Please select a Job ID.'); return; }
    if (header.noOfSamples <= 0) { notify.warning('No. of Samples must be greater than zero.'); return; }
    if (header.defaultQtyPerSample <= 0) { notify.warning('Default Qty/Sample must be greater than zero.'); return; }
    if (!header.visualMatrix) { notify.warning('Please select a VisualMatrix.'); return; }

    const next = generatePreview(header);
    setRows(next);
    // Legacy's grid opens with every generated row ticked (screenshot parity).
    setSelected(new Set(next.map(r => r.key)));
  }, [header, notify]);

  /** Legacy `btnRefresh_Execute` — clear rows, reset the header, KEEP the Job ID. */
  const doRefresh = useCallback(() => {
    setRows([]);
    setSelected(new Set());
    setHeader(h => emptyLabelHeader(h.jobId, ''));
  }, []);

  /** Legacy `btnPrintPreview_Execute` — render server-side, open the PDF blob. */
  const doPrintPreview = useCallback(async () => {
    if (rows.length === 0) {
      notify.warning('Click Display to generate the labels first.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post('/api/v1/sample-registration/label-pdf', {
        reportType,
        templateName: LABEL_TEMPLATE_NAME,
        rows: rows.map(r => ({
          sampleId: r.sampleId, bottleNo: r.bottleNo,
          visualMatrixName: r.visualMatrix, storage: r.storage,
        })),
      }, { responseType: 'blob' });
      const blob = res.data as Blob;
      if (!blob || blob.size === 0) { notify.warning('No label could be generated.'); return; }
      window.open(URL.createObjectURL(blob), '_blank');
    } finally {
      setBusy(false);
    }
  }, [rows, reportType, notify]);

  // ── Grid ──────────────────────────────────────────────────────────────────

  const commit = useCallback((row: LabelRow, col: AppColumn<LabelRow>, value: string) => {
    setRows(current => {
      const target = current.find(r => r.key === row.key);
      if (!target) return current;

      if (col.field === 'noOfQty') {
        const qty = spin(value);
        if (qty === target.noOfQty) return current;
        return applyBottleCount(
          current.map(r => r.key === row.key ? { ...r, noOfQty: qty } : r),
          { ...target, noOfQty: qty });
      }
      if (col.field === 'visualMatrix') {
        if (value === target.visualMatrix) return current;
        return propagateVisualMatrix(
          current.map(r => r.key === row.key ? { ...r, visualMatrix: value } : r),
          { ...target, visualMatrix: value });
      }
      if (col.field === 'storage') {
        return current.map(r => r.key === row.key ? { ...r, storage: value } : r);
      }
      return current;
    });
  }, []);

  /** Legacy "Copy To All Cell" — copy the clicked cell into every TICKED row. */
  const copyToAll = useCallback((field: 'noOfQty' | 'visualMatrix' | 'storage', value: string) => {
    setMenu(null);
    if (selected.size === 0) return;
    setRows(current => {
      let next = current.map(r => selected.has(r.key)
        ? (field === 'noOfQty' ? { ...r, noOfQty: spin(value) } : { ...r, [field]: value })
        : r);
      if (field === 'noOfQty') {
        // Re-shape every touched sample, exactly as an individual edit would.
        const samples = [...new Set(current.filter(r => selected.has(r.key)).map(r => r.sampleId))];
        for (const sid of samples) {
          const anchor = next.find(r => r.sampleId === sid);
          if (anchor) next = applyBottleCount(next, anchor);
        }
      }
      return next;
    });
  }, [selected]);

  const cellMenu = useCallback(
    (field: 'noOfQty' | 'visualMatrix' | 'storage') =>
      (e: ReactMouseEvent<HTMLTableCellElement>, row: LabelRow) => {
        e.preventDefault();
        if (!selected.has(row.key)) return;   // legacy: only on a selected row
        setMenu({ x: e.clientX, y: e.clientY, field, value: String(row[field] ?? '') });
      }, [selected]);

  const columns = useMemo<AppColumn<LabelRow>[]>(() => [
    { field: 'sampleId', header: 'SampleID', width: 190 },
    {
      field: 'noOfQty', header: 'NoOfQty', width: 110,
      edit: { type: 'text' }, onCellContextMenu: cellMenu('noOfQty'),
    },
    { field: 'bottleNo', header: 'BottleNo', width: 110 },
    {
      field: 'visualMatrix', header: 'VisualMatrix', width: 210,
      edit: { type: 'combo', options: matrixOptions },
      onCellContextMenu: cellMenu('visualMatrix'),
    },
    {
      field: 'storage', header: 'Storage', width: 180,
      edit: { type: 'combo', options: storages },
      onCellContextMenu: cellMenu('storage'),
    },
  ], [matrixOptions, storages, cellMenu]);

  if (!open) return null;

  return (
    <AppDialog
      open={open}
      title="Generic Sample Label"
      icon="ti-barcode"
      width={1150}
      maximizable
      onClose={onClose}
    >
      <div className="gsl-body">
        {/* ── LEFT: report-type selector + the bottle grid ─────────────── */}
        <div className="gsl-left">
          <div className="gsl-reportsel">
            <ComboSelect
              value={reportType}
              options={[...LABEL_REPORT_TYPES]}
              // A mode selector always holds a value — ignore a cleared one.
              onChange={v => v && setReportType(v)}
            />
          </div>
          <AppDataGrid<LabelRow>
            rows={rows}
            columns={columns}
            getRowId={r => r.key}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            onCellCommit={commit}
            defaultPageSize={20}
            emptyText="Click Display to generate the labels."
          />
        </div>

        {/* ── RIGHT: the Details panel + the Action buttons ────────────── */}
        <div className="gsl-right">
          <h3 className="gsl-h">Details</h3>
          <div className="gsl-fields">
            <label className="gsl-lbl">Job ID:</label>
            <input className="gsl-in" value={header.jobId} readOnly />

            <label className="gsl-lbl">No. of Samples:</label>
            <input className="gsl-in" type="number" min={1} step={1}
              value={header.noOfSamples}
              onChange={e => patch({ noOfSamples: spin(e.target.value) })} />

            <label className="gsl-lbl">Default Qty/Sample:</label>
            <input className="gsl-in" type="number" min={1} step={1}
              value={header.defaultQtyPerSample}
              onChange={e => patch({ defaultQtyPerSample: spin(e.target.value) })} />

            <label className="gsl-lbl">Storage:</label>
            <ComboSelect value={header.storage} options={storages}
              onChange={v => patch({ storage: v })} />

            <label className="gsl-lbl">Visual Matrix:</label>
            <ComboSelect value={header.visualMatrix} options={matrixOptions}
              onChange={v => patch({ visualMatrix: v })} />
          </div>

          <h3 className="gsl-h">Action</h3>
          <div className="gsl-actions">
            <button className="ca-btn ca-btn-solid" onClick={doDisplay}>Display</button>
            <button className="ca-btn ca-btn-solid" disabled={busy}
              onClick={() => void doPrintPreview()}>Print Preview</button>
            <button className="ca-btn ca-btn-solid" onClick={doRefresh}>Refresh</button>
            <button className="ca-btn ca-btn-solid" onClick={onClose}>Exit</button>
          </div>
        </div>
      </div>

      {/* Legacy row context menu — one item, "Copy To All Cell". */}
      {menu && (
        <>
          <div className="gsl-menu-scrim"
            onClick={() => setMenu(null)}
            onContextMenu={e => { e.preventDefault(); setMenu(null); }} />
          <div className="gsl-menu" style={{ top: menu.y, left: menu.x }}>
            <button className="gsl-menu-item" onClick={() => copyToAll(menu.field, menu.value)}>
              <i className="ti ti-copy" /> Copy To All Cell
            </button>
          </div>
        </>
      )}
    </AppDialog>
  );
}
